import { X, Keyboard, Command, ArrowUp, ArrowDown, CornerDownLeft, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutItemProps {
  keys: React.ReactNode;
  description: string;
}

function ShortcutItem({ keys, description }: ShortcutItemProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-600 dark:text-slate-400">{description}</span>
      <div className="flex items-center gap-1">{keys}</div>
    </div>
  );
}

function Key({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'px-2 py-1 text-xs font-mono font-medium rounded',
        'bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700',
        'text-slate-700 dark:text-slate-300 shadow-sm',
        className
      )}
    >
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 dark:bg-brand-500/20 rounded-xl">
              <Keyboard className="w-5 h-5 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Navigation */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
              Navigation
            </h3>
            <div className="space-y-1">
              <ShortcutItem
                keys={<><Key><ArrowUp className="w-3 h-3" /></Key> or <Key>k</Key></>}
                description="Previous request"
              />
              <ShortcutItem
                keys={<><Key><ArrowDown className="w-3 h-3" /></Key> or <Key>j</Key></>}
                description="Next request"
              />
              <ShortcutItem
                keys={<><Key><Home className="w-3 h-3" /></Key></>}
                description="First request"
              />
              <ShortcutItem
                keys={<Key>End</Key>}
                description="Last request"
              />
              <ShortcutItem
                keys={<><Key><CornerDownLeft className="w-3 h-3" /></Key></>}
                description="Open request details"
              />
            </div>
          </div>

          {/* Actions */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
              Actions
            </h3>
            <div className="space-y-1">
              <ShortcutItem
                keys={<><Key><Command className="w-3 h-3" /></Key> + <Key>O</Key></>}
                description="Open HAR file"
              />
              <ShortcutItem
                keys={<Key>Esc</Key>}
                description="Close sidebar"
              />
              <ShortcutItem
                keys={<Key>?</Key>}
                description="Show keyboard shortcuts"
              />
            </div>
          </div>

          {/* Tips */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-800">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-700 dark:text-slate-300">Pro tip:</span> Use{' '}
              <Key className="inline">j</Key> and <Key className="inline">k</Key> to navigate like a pro.
              These shortcuts work anywhere, even when not focused on the table.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onClose}
            className="w-full btn-primary py-2 px-4 text-sm"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
